// REST / WORK — the citizens' shift. Citizens can't wander the fields for ever: REST sends every citizen to the nearest
// building to sleep (the scene walks them in and fades them out as they enter — see scene/peasants.js); WORK sends them back
// out. Left alone, they go back to work by themselves after RestConfig.durationMs (8 minutes = 8 game hours, 1 minute = 1 hour).
//
// Real consequences, no fiction:
//   • while they rest they earn no wages and add nothing to your clicks (RestConfig.pausesWork);
//   • citizens who are indoors can't be hurt by raiders.
// The end time is a wall-clock timestamp saved with the kingdom, so a reload (or a long absence) can't strand them asleep.

import { Events, emit } from '../ui/eventBus.js'
import { RestConfig } from '../config/balance.js'
import { active, persist } from './state.js'
import { pushLog } from '../ui/hud.js'

let durationMs = RestConfig.durationMs // (a test can shorten it through window.__rest)

const citizenCount = () => Object.values(active().goons ?? {}).reduce((s, n) => s + (Number(n) || 0), 0)
const minutes = (ms) => { const v = Math.round((ms / 60000) * 10) / 10; return `${v} minute${v === 1 ? '' : 's'}` }

export function isResting() {
  const r = active().rest
  return !!r && r.until > Date.now()
}
export function restRemainingMs() {
  const r = active().rest
  return r ? Math.max(0, r.until - Date.now()) : 0
}
// Are the citizens at work right now? (false while they rest and resting pauses work)
export const citizensWorking = () => !(RestConfig.pausesWork && isResting())

export function startRest() {
  if (isResting()) return { ok: false, reason: 'already resting' }
  if (!citizenCount()) {
    pushLog('You have no citizens to rest yet — buy a Peasant or Scribe in CITIZENS first.')
    return { ok: false, reason: 'no citizens' }
  }
  const k = active()
  const now = Date.now()
  k.rest = { startedAt: now, until: now + durationMs }
  persist()
  emit(Events.RestChange, { resting: true, until: k.rest.until, auto: false })
  pushLog(`🌙 Your citizens head indoors to rest. While they sleep they earn nothing and add nothing to your clicks — they go back to work by themselves in ${minutes(durationMs)} (${Math.round(durationMs / RestConfig.gameHourMs)} game hours), or click WORK.`)
  return { ok: true }
}

export function stopRest(auto = false) {
  const k = active()
  if (!k.rest) return false
  k.rest = null
  persist()
  emit(Events.RestChange, { resting: false, auto })
  pushLog(auto
    ? '⚒ The citizens have had their rest and are back at work.'
    : '⚒ Your citizens are back at work.')
  return true
}

export function init() {
  // A rest that ran out while the game was closed is simply over: they are at work when the game opens.
  const k = active()
  if (k.rest && k.rest.until <= Date.now()) { k.rest = null; persist() }
  setInterval(() => {
    const r = active().rest
    if (r && r.until <= Date.now()) stopRest(true)
  }, 1000)
  if (import.meta.env?.DEV) window.__rest = { start: startRest, stop: () => stopRest(false), remaining: restRemainingMs, resting: isResting, setDurationMs: (v) => { durationMs = v } } // dev/test hook
}
